import React from 'react';
import { Button } from '../ui/button';
import { MessageSquare, Sparkles, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { CommentPreviewResponse } from '../../lib/types';
interface CommentsPreviewProps {
  preview: CommentPreviewResponse | null;
  loading: boolean;
  error: string | null;
  alreadyFetched: boolean;
  onFetchClick: () => void;
}
export function CommentsPreview({
  preview,
  loading,
  error,
  alreadyFetched,
  onFetchClick
}: CommentsPreviewProps) {
  if (loading) {
    return <div className="border border-border rounded-lg p-4" data-id="element-978">
        <div className="animate-pulse space-y-3" data-id="element-979">
          <div className="h-4 bg-secondary rounded w-3/4" data-id="element-980"></div>
          <div className="h-4 bg-secondary rounded w-1/2" data-id="element-981"></div>
          <div className="h-20 bg-secondary rounded" data-id="element-982"></div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="border border-destructive/20 rounded-lg p-4 bg-destructive/5" data-id="element-983">
        <div className="flex items-center gap-2 text-destructive mb-2" data-id="element-984">
          <AlertCircle size={16} data-id="element-985" />
          <span className="text-sm font-medium" data-id="element-986">Failed to load preview</span>
        </div>
        <p className="text-xs text-muted-foreground" data-id="element-987">{error}</p>
      </div>;
  }
  if (!preview) return null;
  return <div className="border border-border rounded-lg hover:border-primary/30 transition-colors" data-id="element-988">
      <div className="p-4" data-id="element-989">
        <div className="flex items-start justify-between mb-3" data-id="element-990">
          <div data-id="element-991">
            <div className="flex items-center gap-2 mb-1" data-id="element-992">
              <MessageSquare size={16} className="text-primary" data-id="element-993" />
              <h4 className="font-medium text-sm" data-id="element-994">Comment Data Extraction</h4>
              {alreadyFetched && <span className="text-xs bg-green-500/20 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-995">
                  <CheckCircle size={10} data-id="element-996" />
                  Previously fetched
                </span>}
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-997">
              Preview of first {preview.preview_comments.length} comments
            </p>
          </div>
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full whitespace-nowrap" data-id="element-998">
            {preview.credits_needed}{' '}
            {preview.credits_needed === 1 ? 'credit' : 'credits'}
          </span>
        </div>

        {/* Preview Comments */}
        <div className="space-y-2 mb-4" data-id="element-999">
          {preview.preview_comments.slice(0, 3).map((comment, index) => <div key={index} className="bg-secondary/30 rounded-lg p-3 text-sm" data-id="element-1000">
              <div className="flex items-start justify-between mb-1" data-id="element-1001">
                <span className="font-medium text-xs" data-id="element-1002">
                  {comment.author_name || comment.author}
                </span>
                <span className="text-xs text-muted-foreground flex items-center gap-1" data-id="element-1003">
                  <TrendingUp size={10} data-id="element-1004" />
                  {comment.likes.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2" data-id="element-1005">
                {comment.text || comment.comment}
              </p>
            </div>)}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-secondary/20 rounded-lg" data-id="element-1006">
          <div data-id="element-1007">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1008">
              Estimated Total
            </div>
            <div className="text-lg font-semibold" data-id="element-1009">
              {preview.estimated_total.toLocaleString()}
            </div>
          </div>
          <div data-id="element-1010">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1011">
              Credits Needed
            </div>
            <div className="text-lg font-semibold text-primary" data-id="element-1012">
              {preview.credits_needed}
            </div>
          </div>
        </div>

        {/* Info Message */}
        <div className="mb-4 p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-600 dark:text-blue-400" data-id="element-1013">
          <Sparkles size={12} className="inline mr-1" data-id="element-1014" />
          {preview.message}
        </div>

        {alreadyFetched && <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2" data-id="element-1015">
            <AlertCircle size={14} data-id="element-1016" />
            You've already fetched comments for this video. Fetching again will
            use {preview.credits_needed} more{' '}
            {preview.credits_needed === 1 ? 'credit' : 'credits'}.
          </div>}

        {/* CTA Button */}
        <Button onClick={onFetchClick} className="w-full" size="sm" data-id="element-1017">
          <MessageSquare size={14} className="mr-2" data-id="element-1018" />
          {alreadyFetched ? `Fetch Again (${preview.credits_needed} ${preview.credits_needed === 1 ? 'credit' : 'credits'})` : `Fetch All Comments (${preview.credits_needed} ${preview.credits_needed === 1 ? 'credit' : 'credits'})`}
        </Button>
      </div>
    </div>;
}